/**
 * Subscription Gate component for trAInedup.ai
 * Controls access to features based on subscription tier
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Crown, Zap, Star } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Card } from './Card';
import { Button } from './Button';
import { SubscriptionTier, SUBSCRIPTION_TIERS } from '../../services/stripe/config';
import { ROUTES } from '../../utils/constants';

interface SubscriptionGateProps {
  children: React.ReactNode;
  feature?: string;
  requiredTier?: SubscriptionTier;
  fallback?: React.ReactNode;
  showUpgrade?: boolean;
}

/**
 * Subscription gate component
 * Renders children if user has required subscription, otherwise shows upgrade prompt
 */
export const SubscriptionGate: React.FC<SubscriptionGateProps> = ({
  children,
  feature,
  requiredTier,
  fallback,
  showUpgrade = true,
}) => {
  const { hasFeatureAccess, canAccessTier } = useSubscription();

  // Check access based on feature or tier
  const hasAccess = feature 
    ? hasFeatureAccess(feature)
    : requiredTier 
      ? canAccessTier(requiredTier)
      : true;

  // If user has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If custom fallback is provided, use it
  if (fallback) {
    return <>{fallback}</>;
  }

  // If showUpgrade is false, render nothing
  if (!showUpgrade) {
    return null;
  }

  // Default upgrade prompt
  return <UpgradePrompt feature={feature} requiredTier={requiredTier} />;
};

/**
 * Upgrade prompt component
 */
interface UpgradePromptProps {
  feature?: string;
  requiredTier?: SubscriptionTier;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  feature,
  requiredTier,
  size = 'md',
  className = '',
}) => {
  const { getCurrentTier, subscribe } = useSubscription();
  const currentTier = getCurrentTier();

  /**
   * Get upgrade information based on feature or tier
   */
  const getUpgradeInfo = () => {
    // Feature-based upgrade info
    if (feature) {
      const featureUpgrades: Record<string, { tier: SubscriptionTier; title: string; description: string }> = {
        'expert_consultations': {
          tier: SUBSCRIPTION_TIERS.PREMIUM,
          title: 'Expert Consultations',
          description: 'Get 1-on-1 guidance from AI experts to accelerate your learning.',
        },
        'custom_learning_paths': {
          tier: SUBSCRIPTION_TIERS.PREMIUM,
          title: 'Custom Learning Paths',
          description: 'Personalized learning journeys tailored to your goals and skill level.',
        },
        'certificates': {
          tier: SUBSCRIPTION_TIERS.PREMIUM,
          title: 'Certificates',
          description: 'Earn verified certificates to showcase your AI expertise.',
        },
        'team_management': {
          tier: SUBSCRIPTION_TIERS.CORPORATE,
          title: 'Team Management',
          description: 'Manage your team\'s learning progress and assign courses.',
        },
        'advanced_analytics': {
          tier: SUBSCRIPTION_TIERS.CORPORATE,
          title: 'Advanced Analytics',
          description: 'Deep insights into learning patterns and performance metrics.',
        },
        'white_label': {
          tier: SUBSCRIPTION_TIERS.CORPORATE,
          title: 'White Label Solution',
          description: 'Customize the platform with your branding and domain.',
        },
      };

      return featureUpgrades[feature] || {
        tier: SUBSCRIPTION_TIERS.PREMIUM,
        title: 'Premium Feature',
        description: 'This feature is available with a premium subscription.',
      };
    }

    // Tier-based upgrade info
    if (requiredTier) {
      const tierInfo: Record<SubscriptionTier, { title: string; description: string }> = {
        [SUBSCRIPTION_TIERS.FREE]: {
          title: 'Free Plan',
          description: 'Basic access to AI learning resources.',
        },
        [SUBSCRIPTION_TIERS.STANDARD]: {
          title: 'Standard Plan',
          description: 'AI fundamentals and monthly training packs.',
        },
        [SUBSCRIPTION_TIERS.PREMIUM]: {
          title: 'Premium Plan',
          description: 'Advanced courses, expert consultations, and certificates.',
        },
        [SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE]: {
          title: '12-Month Package',
          description: 'One-time payment for 12 months of full access.',
        },
        [SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME]: {
          title: 'Stackcommerce Lifetime',
          description: 'Lifetime access redeemed through activation codes.',
        },
        [SUBSCRIPTION_TIERS.CORPORATE]: {
          title: 'Corporate Plan',
          description: 'Team management, analytics, and white-label solutions.',
        },
        [SUBSCRIPTION_TIERS.ANNUAL]: {
          title: 'Annual Plan',
          description: 'Yearly access to AI learning resources.',
        },
      };

      return {
        tier: requiredTier,
        ...tierInfo[requiredTier],
      };
    }

    // Default upgrade info
    return {
      tier: SUBSCRIPTION_TIERS.PREMIUM,
      title: 'Premium Feature',
      description: 'Upgrade your subscription to access this feature.',
    };
  };

  const upgradeInfo = getUpgradeInfo();

  /**
   * Get tier icon
   */
  const getTierIcon = (tier: SubscriptionTier) => {
    switch (tier) {
      case SUBSCRIPTION_TIERS.STANDARD:
        return <Star className="w-6 h-6 text-yellow-500" />;
      case SUBSCRIPTION_TIERS.PREMIUM:
        return <Zap className="w-6 h-6 text-blue-500" />;
      case SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME:
        return <Zap className="w-6 h-6 text-green-600" />;
      case SUBSCRIPTION_TIERS.CORPORATE:
        return <Crown className="w-6 h-6 text-purple-500" />;
      default:
        return <Lock className="w-6 h-6 text-neutral-400" />;
    }
  };

  /**
   * Handle upgrade click
   */
  const handleUpgrade = async () => {
    try {
      if (upgradeInfo.tier === SUBSCRIPTION_TIERS.CORPORATE) {
        window.location.assign('/corporate-request');
        return;
      }
      if (upgradeInfo.tier === SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME) {
        window.location.assign('/dashboard/billing');
        return;
      }
      await subscribe(upgradeInfo.tier);
    } catch (error) {
      console.error('Error starting subscription:', error);
    }
  };

  /**
   * Get size classes
   */
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'p-4',
          icon: 'w-8 h-8',
          title: 'text-lg',
          description: 'text-sm',
          button: 'text-sm',
        };
      case 'lg':
        return {
          container: 'p-8',
          icon: 'w-12 h-12',
          title: 'text-2xl',
          description: 'text-base',
          button: 'text-base',
        };
      default:
        return {
          container: 'p-6',
          icon: 'w-10 h-10',
          title: 'text-xl',
          description: 'text-sm',
          button: 'text-sm',
        };
    }
  };

  const sizeClasses = getSizeClasses();

  return (
    <Card className={`text-center bg-gradient-to-br from-neutral-50 to-primary-50 border-primary-200 ${className}`}>
      <div className={sizeClasses.container}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-white rounded-full shadow-sm">
            {getTierIcon(upgradeInfo.tier)}
          </div>
        </div>

        {/* Content */}
        <h3 className={`font-montserrat font-semibold text-neutral-800 mb-2 ${sizeClasses.title}`}>
          {upgradeInfo.title}
        </h3>
        
        <p className={`text-neutral-600 mb-6 ${sizeClasses.description}`}>
          {upgradeInfo.description}
        </p>
        <div className="mb-6 p-3 bg-white rounded-lg border border-neutral-200">
          <p className="font-medium text-neutral-800">Current plan: <span className="capitalize">{currentTier}</span></p>
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          <Button
            variant="primary"
            fullWidth
            onClick={handleUpgrade}
            className={sizeClasses.button}
          >
            Upgrade to {upgradeInfo.tier.charAt(0).toUpperCase() + upgradeInfo.tier.slice(1)}
          </Button>
          
          <Link to={ROUTES.pricing}>
            <Button
              variant="outline"
              fullWidth
              className={sizeClasses.button}
            >
              View All Plans
            </Button>
          </Link>
        </div>

        {/* Help text */}
        <p className="text-xs text-neutral-500 mt-4">
          Questions? <Link to={ROUTES.contact} className="text-primary hover:underline">Contact support</Link>
        </p>
      </div>
    </Card>
  );
};

/**
 * Feature badge component for indicating premium features
 */
interface FeatureBadgeProps {
  tier: SubscriptionTier;
  className?: string;
}

export const FeatureBadge: React.FC<FeatureBadgeProps> = ({ tier, className = '' }) => {
  const getBadgeColor = (tier: SubscriptionTier) => {
    switch (tier) {
      case SUBSCRIPTION_TIERS.STANDARD:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case SUBSCRIPTION_TIERS.PREMIUM:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case SUBSCRIPTION_TIERS.CORPORATE:
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getBadgeColor(tier)} ${className}`}>
      <Lock className="w-3 h-3 mr-1" />
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </span>
  );
};

export default SubscriptionGate;
